import React, { useState, useEffect } from 'react';
import { getEmailStatistics, EmailStatistics } from '../../services/emailService';
import './EmailMetrics.css';

const ProfessorContactMetrics: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<EmailStatistics | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await getEmailStatistics();
        setStats(data);
      } catch (err) {
        console.error('Error fetching email statistics:', err);
        setError('Failed to load professor contact metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    
    // Refresh stats every 5 minutes
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading professor contact metrics...</p>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="alert alert-danger" role="alert">
        {error || 'Failed to load professor contact metrics'}
      </div>
    );
  }

  // Calculate additional metrics
  const averageResponseTime = stats.top_professors.length > 0 
    ? stats.top_professors.reduce((sum, prof) => sum + (prof.response_score || 0), 0) / stats.top_professors.length
    : 0;

  return (
    <div className="email-metrics">
      <h2 className="section-title">Professor Contact Metrics</h2>
      
      <div className="row">
        <div className="col-md-3 mb-4">
          <div className="metric-card">
            <div className="metric-header">
              <i className="bi bi-envelope"></i>
              <h3>Total Professors Contacted</h3>
            </div>
            <div className="metric-value">{stats.total_emails}</div>
          </div>
        </div>
        
        <div className="col-md-3 mb-4">
          <div className="metric-card">
            <div className="metric-header">
              <i className="bi bi-envelope-open"></i>
              <h3>Open Rate</h3>
            </div>
            <div className="metric-value">
              {(stats.open_rate * 100).toFixed(1)}%
              <span className="metric-detail">({stats.opened_emails} opened)</span>
            </div>
          </div>
        </div>
        
        <div className="col-md-3 mb-4">
          <div className="metric-card">
            <div className="metric-header">
              <i className="bi bi-reply"></i>
              <h3>Response Rate</h3>
            </div>
            <div className="metric-value">
              {(stats.response_rate * 100).toFixed(1)}%
              <span className="metric-detail">({stats.responded_emails} responses)</span>
            </div>
          </div>
        </div>
        
        <div className="col-md-3 mb-4">
          <div className="metric-card">
            <div className="metric-header">
              <i className="bi bi-graph-up"></i>
              <h3>Avg. Response Score</h3>
            </div>
            <div className="metric-value">
              {(averageResponseTime * 100).toFixed(0)}%
            </div>
          </div>
        </div>
      </div>
      
      <h3 className="mt-4 mb-3">Top Responsive Professors</h3>
      {stats.top_professors.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>Name</th>
                <th>University</th>
                <th>Department</th>
                <th>Emails Sent</th>
                <th>Response Score</th>
              </tr>
            </thead>
            <tbody>
              {stats.top_professors.map(professor => (
                <tr key={professor.id}>
                  <td>
                    <a href={`/professors/${professor.id}`}>{professor.name}</a>
                  </td>
                  <td>{professor.university || 'N/A'}</td>
                  <td>{professor.department || 'N/A'}</td>
                  <td>{professor.emails_sent}</td>
                  <td>
                    <div className="progress" style={{ height: '8px' }}>
                      <div 
                        className="progress-bar" 
                        role="progressbar" 
                        style={{ 
                          width: `${professor.response_score * 100}%`,
                          backgroundColor: professor.response_score > 0.7 ? '#28a745' : 
                                          professor.response_score > 0.4 ? '#ffc107' : '#dc3545'
                        }}
                        aria-valuenow={professor.response_score * 100}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      ></div>
                    </div>
                    <small className="text-muted">{(professor.response_score * 100).toFixed(0)}%</small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="alert alert-info">
          No professor responses recorded yet. Start sending emails to track responses!
        </div>
      )}
      
      <div className="mt-4 tips-section">
        <h4>Tips to Improve Response Rates</h4>
        <ul className="list-group">
          <li className="list-group-item">
            <i className="bi bi-lightbulb me-2"></i>
            Personalize your emails by mentioning specific research papers or projects
          </li>
          <li className="list-group-item">
            <i className="bi bi-clock me-2"></i>
            Send emails during weekdays, preferably in the morning
          </li>
          <li className="list-group-item">
            <i className="bi bi-chat-text me-2"></i>
            Keep your emails concise and include specific questions
          </li>
          <li className="list-group-item">
            <i className="bi bi-graph-up me-2"></i>
            Follow up after 7-10 days if you don't receive a response
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ProfessorContactMetrics;
